// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { QuoteType } from "@insite/client-framework/Services/QuoteService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import createOrRequestQuote from "@insite/client-framework/Store/Pages/RfqRequestQuote/Handlers/CreateOrRequestQuote";
import updateQuoteParameter from "@insite/client-framework/Store/Pages/RfqRequestQuote/Handlers/UpdateQuoteParameter";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup/RadioGroup";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import FieldSetPresentationProps from "@insite/mobius/utilities/fieldSetProps";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, FC, ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    cart: getCurrentCartState(state).value,
    accounts: state.pages.rfqRequestQuote.accounts,
    rfqQuoteDetailsPageUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
    rfqQuoteConfirmationPageUrl: getPageLinkByPageType(state, "RfqConfirmationPage")?.url,
    jobQuoteEnabled: getSettingsCollection(state).quoteSettings.jobQuoteEnabled,
    isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
});

const mapDispatchToProps = {
    updateQuoteParameter,
    createOrRequestQuote,
};

type Props = ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps> & WidgetProps;

export interface RfqRequestQuoteSpecificationsStyles {
    quoteTypeRadioGroup?: RadioGroupProps;
    quoteTypeRadioButton?: FieldSetPresentationProps<RadioComponentProps>;
    jobNameTextField?: TextFieldPresentationProps;
    assignUserSelect?: SelectPresentationProps;
    notesTextArea?: TextAreaProps;
    submitButton?: ButtonPresentationProps;
    actionsTopSection?: InjectableCss;
    actionsBottomSection?: InjectableCss;
}

export const rfqRequestQuoteSpecificationsStyles: RfqRequestQuoteSpecificationsStyles = {
    quoteTypeRadioGroup: {
        css: css`
            legend {
                display: none;
            }
            @media (max-width: 850px) {
                legend {
                    display: block;
                }
            }

            @media print {
                legend {
                    display: block;
                }
            }
        `,
    },
    quoteTypeRadioButton: {
        mergeCss: true,
        css: css`
            @media (max-width: 850px) {
                display: block !important;
            }
        `,
    },
    submitButton: {
        css: css`
            @media (max-width: 1024px) {
                white-space: nowrap;
            }
            @media (max-width: 481px) {
                width: 100%;
            }
        `,
    },
    jobNameTextField: {
        cssOverrides: {
            formField: css`
                max-width: 323px;
                margin-bottom: 15px;
            `,
        },
    },
    assignUserSelect: {
        cssOverrides: {
            formField: css`
                max-width: 323px;
                @media (max-width: 520px) {
                    max-width: unset;
                }
                & {
                    label {
                        font-weight: 500;
                    }
                }
            `,
        },
    },
    notesTextArea: {
        cssOverrides: {
            formField: css`
                margin-right: 15px;
                max-width: 323px;
                label {
                    font-weight: 500;
                }
                textarea::placeholder {
                    opacity: 0.6;
                }
                &&& textarea {
                    padding: 9px;
                    max-height: 40px !important;
                }
                margin-top: -24px;
                @media (max-width: 900px) {
                    max-width: unset;
                }
                @media (max-width: 520px) {
                    margin-right: 0px;
                    margin-bottom: 15px;
                }
            `,
            inputSelect: css`
                resize: none;
            `,
        },
    },
    actionsTopSection: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: flex-end;
            margin-bottom: 10px;
            @media (max-width: 900px) {
                justify-content: flex-start;
            }
            @media (max-width: 600px) {
                flex-wrap: wrap;
            }
            @media (max-width: 520px) {
                margin-bottom: 15px;
            }
        `,
    },
    actionsBottomSection: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: flex-end;
            @media (max-width: 900px) {
                justify-content: flex-start;
            }
        `,
    },
};

const styles = rfqRequestQuoteSpecificationsStyles;

const RfqRequestQuoteSpecifications: FC<Props> = ({
    cart,
    accounts,
    updateQuoteParameter,
    createOrRequestQuote,
    history,
    rfqQuoteDetailsPageUrl,
    rfqQuoteConfirmationPageUrl,
    jobQuoteEnabled,
    isUpdatingCartLine,
}) => {
    const [notes, setNotes] = useState(cart?.notes);
    const [jobName, setJobName] = useState("");
    const [jobNameError, setJobNameError] = useState<ReactNode>("");
    const [quoteType, setQuoteType] = useState<QuoteType>("quote");
    const [accountId, setAccountId] = useState<string | undefined>(undefined);
    const [accountIdError, setAccountIdError] = useState<ReactNode>("");
    const [isLoading, setIsLoading] = useState(false);

    const dispatch = useAppDispatch();

    const saveParameter = () => {
        updateQuoteParameter({
            quoteId: cart?.id || "",
            quoteType,
            accountId,
            jobName,
            note: notes || "",
        });
    };
    useEffect(saveParameter, [notes, jobName, quoteType, accountId, cart?.id]);

    if (!cart) {
        return null;
    }

    const isCreatingQuote = cart.isSalesperson;

    const notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };

    const quoteTypeChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setQuoteType(event.currentTarget.value as QuoteType);
    };

    const userChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        const id = event.currentTarget.value;
        setAccountId(id);
        validateUser(id);
    };

    const validateUser = (userId: string) => {
        const valid = !isCreatingQuote || userId.trim();
        setAccountIdError(valid ? "" : siteMessage("Rfq_UserIsRequired"));
        return valid;
    };

    const jobNameChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const name = event.currentTarget.value;
        setJobName(name);
        validateJobName(name);
    };

    const validateJobName = (jobName: string) => {
        const valid = quoteType !== "job" || jobName.trim();
        setJobNameError(valid ? "" : siteMessage("Rfq_Job_Name_required"));
        return valid;
    };

    const canSubmit = cart.cartLines && cart.cartLines.length > 0 && !isUpdatingCartLine;
    const submitHandler = () => {
        if (!canSubmit || !validateJobName(jobName) || !validateUser(accountId || "")) {
            return;
        }
        setIsLoading(true);
        dispatch({ type: "commonActionSetter/SetAction", payload: { name: "isLoading", value: true } });
        createOrRequestQuote({
            onSuccess: (result: { id: string }) => {
                setIsLoading(false);
                dispatch({ type: "commonActionSetter/SetAction", payload: { name: "isLoading", value: false } });
                if (isCreatingQuote && rfqQuoteDetailsPageUrl) {
                    history.push(`${rfqQuoteDetailsPageUrl}?quoteId=${result.id}`);
                } else if (!isCreatingQuote && rfqQuoteConfirmationPageUrl) {
                    history.push(`${rfqQuoteConfirmationPageUrl}?quoteId=${result.id}`);
                }
            },
        });
    };

    const submitButtonClickHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        submitHandler();
    };

    return (
        <form onSubmit={submitHandler}>
            <StyledWrapper {...styles.actionsTopSection}>
                <TextArea
                    {...styles.notesTextArea}
                    label={translate("Order Notes")}
                    placeholder={translate("You can start typing here")}
                    value={notes}
                    rows={1}
                    maxLength={150}
                    onChange={notesChangeHandler}
                    data-test-selector="requestQuoteOrderNotes"
                />

                <Button
                    {...styles.submitButton}
                    onClick={submitButtonClickHandler}
                    disabled={!canSubmit}
                    data-test-selector="requestQuoteSubmitButton"
                >
                    {isLoading ? (
                        <LoadingSpinner color="secondary" size={28} />
                    ) : (
                        translate(isCreatingQuote ? "Create Quote" : "Submit Request")
                    )}
                </Button>
            </StyledWrapper>
            <StyledWrapper {...styles.actionsBottomSection}>
                {jobQuoteEnabled && (
                    <RadioGroup
                        {...styles.quoteTypeRadioGroup}
                        label={translate("Quote Type")}
                        value={quoteType}
                        horizontal={true}
                        onChangeHandler={quoteTypeChangeHandler}
                        data-test-selector="requestQuoteTypeRadio"
                    >
                        <Radio {...styles.quoteTypeRadioButton} value="quote">
                            {translate("Sales Quote")}
                        </Radio>
                        <Radio {...styles.quoteTypeRadioButton} value="job">
                            {translate("Job Quote")}
                        </Radio>
                    </RadioGroup>
                )}
                {quoteType === "job" && (
                    <TextField
                        {...styles.jobNameTextField}
                        label={translate("Job Name")}
                        required={true}
                        error={jobNameError}
                        placeholder={translate("Job Name Goes Here")}
                        onChange={jobNameChangeHandler}
                        data-test-selector="requestQuoteJobName"
                    />
                )}
                {isCreatingQuote && (
                    <Select
                        label={translate("Assign User")}
                        required={true}
                        {...styles.assignUserSelect}
                        value={accountId}
                        error={accountIdError}
                        onChange={userChangeHandler}
                        data-test-selector="requestQuoteAssignUserSelect"
                    >
                        <option key="" value="">
                            {translate("Select a User")}
                        </option>
                        {accounts?.map(o => {
                            return (
                                <option key={o.id} value={o.id}>
                                    {o.firstName && o.lastName ? `${o.firstName} ${o.lastName}` : o.userName}
                                </option>
                            );
                        })}
                    </Select>
                )}
            </StyledWrapper>
        </form>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RfqRequestQuoteSpecifications)),
    definition: {
        group: "RFQ Request Quote",
        displayName: "Specifications",
        allowedContexts: ["RfqRequestQuotePage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
